import React from 'react'
import type { NewsRaceCard, NewsTournamentPick } from '../../services/newsService'
import { useTranslation } from '../../hooks/use-i18n'
import { F1_MEDAL, f1CountryFlag, f1DriverShort, f1TeamColor } from './f1NewsUi'

interface Props {
  card: NewsRaceCard
}

const SectionLabel: React.FC<{ children: string }> = ({ children }) => (
  <p className="text-center text-[11px] font-semibold tracking-widest text-muted-foreground uppercase mb-2 sm:mb-3">
    {children}
  </p>
)

const NewsRaceCardView: React.FC<Props> = ({ card }) => {
  const { t } = useTranslation()
  const flag = f1CountryFlag(card.country)
  const p1 = card.p1Picks || []
  const p2 = card.p2Picks || []
  const p3 = card.p3Picks || []
  const podium = card.podiumPicks || []
  const constructors = card.constructorPicks || []
  const grid = card.consensusGrid || []
  const classification = card.result || card.liveResults || []
  const maxP1 = p1.length ? p1[0].votes : 0
  const headline = p1.slice(0, 3)

  const statusLabel =
    card.status === 'finished'
      ? `🏁 ${t('news.tab.past')}`
      : card.status === 'in-progress'
        ? `🔴 ${t('formula1.live')}`
        : `🕒 ${t('news.tab.upcoming')}`

  return (
    <article className="relative bg-card border border-border shadow-lg rounded-2xl p-3 sm:p-6">
      <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground gap-2">
        <span className="font-semibold text-primary inline-flex items-center gap-1.5">
          <span aria-hidden>🏎️</span>
          {t('news.f1.championship')}
        </span>
        <span>
          {card.dateLabel} · {card.timeLabel}
        </span>
      </div>
      <div className="h-px bg-border my-2 sm:my-3" />

      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('news.f1.round', { day: String(card.day) })}
          </p>
          <h3 className="text-lg sm:text-2xl font-extrabold text-foreground flex items-center gap-2 flex-wrap">
            <span className="text-2xl sm:text-3xl leading-none" aria-hidden>
              {flag}
            </span>
            {t('news.f1.grandPrix', { city: card.city })}
          </h3>
          {card.country ? (
            <p className="text-sm text-muted-foreground">{card.country}</p>
          ) : null}
        </div>
        <span className="shrink-0 text-xs font-semibold px-2 py-1 rounded-full bg-muted">
          {statusLabel}
        </span>
      </div>

      {card.aiBrief && (
        <div className="mb-3 rounded-xl bg-primary/5 border border-primary/15 px-3 py-2.5 text-sm text-foreground leading-relaxed">
          <span className="text-[10px] uppercase tracking-wide text-primary font-semibold mr-2">AI</span>
          {card.aiBrief}
        </div>
      )}

      <SectionLabel>{t('news.f1.raceWinner')}</SectionLabel>
      {headline.length > 0 ? (
        <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mb-3">
          {headline.map((pick, i) => (
            <div key={pick.name} className="text-center min-w-0">
              <div className="text-xl sm:text-2xl" aria-hidden>
                {F1_MEDAL[i] || '🏎️'}
              </div>
              <p className="text-sm sm:text-lg font-extrabold text-foreground truncate">
                {f1DriverShort(pick.name)}
              </p>
              {pick.team ? (
                <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{pick.team}</p>
              ) : null}
              <p className="text-xs font-semibold mt-0.5" style={{ color: f1TeamColor(pick.team) }}>
                {t('news.predictionsPct', { count: String(pick.votes), pct: String(pick.pct) })}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-muted-foreground mb-3">{t('news.empty.formula1.upcoming')}</p>
      )}

      <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
        {p1.slice(0, 6).map((pick) => (
          <span
            key={`pill-${pick.name}`}
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-border text-xs font-medium text-foreground"
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: f1TeamColor(pick.team) }} />
            <span className="truncate max-w-[120px]">{pick.name}</span>
            <span className="font-bold">{pick.votes}</span>
          </span>
        ))}
        <span className="text-xs text-muted-foreground ml-1">
          {t('news.f1.grids', { count: String(card.totalPredictions) })}
        </span>
      </div>

      {(p2[0] || p3[0] || podium.length > 0) && (
        <div className="mt-4">
          <SectionLabel>{t('news.f1.podiumPicks')}</SectionLabel>
          <div className="grid grid-cols-3 gap-1.5 sm:gap-3">
            {([p1[0], p2[0], p3[0]] as Array<NewsTournamentPick | undefined>).map((pick, i) => (
              <div
                key={`podium-${i}`}
                className="rounded-lg sm:rounded-xl border border-border p-1.5 sm:p-3 text-center bg-muted/40"
              >
                <div className="text-lg sm:text-xl" aria-hidden>
                  {F1_MEDAL[i]}
                </div>
                <div className="text-[10px] font-bold text-muted-foreground">P{i + 1}</div>
                <div className="text-xs sm:text-sm font-bold text-foreground truncate">
                  {pick ? f1DriverShort(pick.name) : '—'}
                </div>
                {pick?.team ? (
                  <div className="text-[10px] text-muted-foreground truncate">{pick.team}</div>
                ) : null}
                {pick ? (
                  <div className="text-[10px] sm:text-xs font-semibold mt-0.5">
                    {pick.pct}%
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {grid.length > 0 && (
        <div className="mt-4">
          <SectionLabel>{t('news.f1.communityGrid')}</SectionLabel>
          <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
            {grid.map((slot) => (
              <div
                key={`grid-${slot.position}`}
                className={`rounded-lg border p-1.5 text-center bg-muted/40 ${
                  slot.position <= 3 ? 'border-primary/40' : 'border-border'
                }`}
              >
                <div className="text-[10px] font-bold text-muted-foreground">
                  {slot.position <= 3 ? F1_MEDAL[slot.position - 1] : `P${slot.position}`}
                </div>
                <div className="text-[11px] sm:text-xs font-bold text-foreground truncate">
                  {f1DriverShort(slot.driver)}
                </div>
                <div className="text-[10px] text-muted-foreground">{slot.pct}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {p1.length > 0 && (
        <div className="mt-4">
          <SectionLabel>{t('news.f1.p1Distribution')}</SectionLabel>
          <div className="space-y-1 sm:space-y-1.5">
            {p1.map((pick) => (
              <div key={`bar-${pick.name}`} className="flex items-center gap-2">
                <span className="w-20 sm:w-28 shrink-0 text-xs text-muted-foreground truncate">
                  {f1DriverShort(pick.name)}
                </span>
                <div className="flex-1 h-4 sm:h-5 rounded bg-muted/50 overflow-hidden">
                  <div
                    className="h-full rounded"
                    style={{
                      width: `${maxP1 > 0 ? Math.max(2, (pick.votes / maxP1) * 100) : 0}%`,
                      backgroundColor: f1TeamColor(pick.team),
                    }}
                  />
                </div>
                <span className="w-8 shrink-0 text-xs font-semibold text-foreground">{pick.votes}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {constructors.length > 0 && (
        <div className="mt-4">
          <SectionLabel>{t('news.f1.constructors')}</SectionLabel>
          <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
            {constructors.map((pick) => (
              <span
                key={pick.name}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border text-xs font-medium"
              >
                <span aria-hidden>🏆</span>
                <span>{pick.name}</span>
                <span className="font-bold">{pick.pct}%</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {classification.length > 0 && (
        <div className="mt-4">
          <SectionLabel>
            {card.status === 'in-progress' ? t('news.f1.liveClassification') : t('news.f1.officialResult')}
          </SectionLabel>
          <ol className="space-y-1">
            {classification.slice(0, 10).map((row) => (
              <li
                key={`${row.position}-${row.driver}`}
                className="flex items-center gap-2 text-sm text-foreground"
              >
                <span className="w-8 font-bold text-muted-foreground">
                  {row.position <= 3 ? F1_MEDAL[row.position - 1] : `P${row.position}`}
                </span>
                <span className="font-semibold truncate">{row.driver}</span>
                {row.team ? (
                  <span className="ml-auto text-xs text-muted-foreground truncate">{row.team}</span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      )}
    </article>
  )
}

export default NewsRaceCardView
